// src/views/menage/CarteMenageAgent.jsx — la carte d'un ménage, côté AGENT,
// d'après la maquette NEST v677 (`carteRun`, journée restructurée du 28/10).
//
// ⚠️ POURQUOI UNE CARTE À PART, ET PAS `RunCard`. `RunCard` sert les écrans de
// l'ORCHESTRATEUR (le détail d'un jour, la frise, le mois) : elle porte un badge
// d'agent, un sélecteur de réassignation, un repère d'exception de règle, les
// heures réelles du voyageur. Rien de tout ça n'aide Sofia à faire son ménage —
// et cet écran-ci se tient d'une main, devant une porte.
//
// ⚠️ CE QUE L'AGENT NE VOIT JAMAIS ICI : le voyageur (journal de la maquette :
// « jamais le voyageur »), l'heure réelle d'arrivée (« À finir avant 16h » :
// l'heure limite est la prochaine arrivée possible), pourquoi l'heure est
// celle-là, et le moindre montant.

import { useState } from "react"
import {
  rangDuMenage, heureEntete, libelleArrivants,
} from "../../lib/menage-journee-agent"
import { heureCourte, heureFinParis } from "../../lib/agent-journee-regles"
import BlocAApporter from './BlocAApporter'
import { heureEffective, formaterDuree } from "../../lib/menage-frise"
import { Users } from "lucide-react"
import {
  CartePlanning, PhraseCarte, LigneAcces, BoutonCarte, BoutonIncident, LienCarte, FeuilleAgent,
} from './CartePlanning'
import { NEST, POLICE } from './palette-nest'

// La PROGRESSION de la checklist — tolérante à la double-forme d'items_checked
// (tableau d'items cochés OU booléen legacy « pièce entière »).
export function progressionChecklist(run, info) {
  const pieces = Array.isArray(info?.menage?.pieces) ? info.menage.pieces : []
  let total = 0
  let coches = 0
  for (const piece of pieces) {
    const items = Array.isArray(piece?.items) ? piece.items : []
    total += items.length
    const v = (run?.items_checked || {})[piece.id]
    if (v === true) coches += items.length
    else if (Array.isArray(v)) coches += Math.min(v.length, items.length)
  }
  return { coches, total }
}

/** Le texte du bouton : il dit toujours où on en est. */
export function etatBouton(run, info) {
  const fait = run?.status === 'completed'
  const { coches, total } = progressionChecklist(run, info)
  const enCours = !fait && (coches > 0 || run?.status === 'in_progress')
  return {
    etat: fait ? 'fini' : enCours ? 'en-cours' : 'a-commencer',
    libelle: fait ? 'Voir la checklist'
      : enCours ? `Reprendre la checklist${total > 0 ? ` · ${coches}/${total}` : ''}`
      : 'Commencer le ménage',
  }
}

export default function CarteMenageAgent({
  run, index, info, habit = 'grand', onCommencer, onIncident, consignes = [], ancre, indisponible = false,
}) {
  const [consignesOuvertes, setConsignesOuvertes] = useState(false)
  const duree = Number(run?.duree_min) > 0 ? Number(run.duree_min) : null
  const timing = info?.timing || null
  // L'heure EFFECTIVE : recalée si un accord a rétréci la plage après coup.
  // Même règle que la frise — deux écrans ne doivent pas afficher deux heures
  // différentes du même ménage.
  const { debut } = heureEffective({
    heureFixee: run?.heure_fixee,
    dureeMin: duree,
    plageDebut: timing?.checkout_time,
    plageFin: timing?.checkin_time,
  })
  const entete = heureEntete({
    debutMin: debut, plageDebut: timing?.checkout_time, plageFin: timing?.checkin_time,
  })
  const limite = heureCourte(timing?.checkin_time)
  const arrivants = libelleArrivants(timing?.arrivants)
  const code = (info?.codes_acces?.boite_cles && String(info.codes_acces.boite_cles).trim()) || null
  const rang = rangDuMenage(index)
  const fait = run?.status === 'completed'
  const nom = info?.nom || run.logement_nom || 'Un logement'
  const fin = fait ? heureFinParis(run?.completed_at) : null
  const sousTitre = [rang, duree ? formaterDuree(duree) : null, fin ? `terminé à ${fin}` : null].filter(Boolean).join(' · ')
  const bouton = etatBouton(run, info)
  const pastilleConsignes = consignes.length > 0 && !fait && (
    <button type="button" data-testid={`carte-consignes-${run.id}`} onClick={() => setConsignesOuvertes(true)}
      style={{ background: '#fff', border: `1px solid ${NEST.boutonBord}`, borderRadius: 12, padding: '3px 9px', minHeight: 30,
        fontSize: 11, color: NEST.boutonTexte, fontFamily: POLICE, cursor: 'pointer', flexShrink: 0 }}>
      {consignes.length} consigne{consignes.length > 1 ? 's' : ''}
    </button>
  )

  return (
    <div ref={ancre}>
      <CartePlanning testId={`carte-menage-${run.id}`} habit={habit} heure={entete?.texte} titre={nom} sousTitre={sousTitre}
        droite={fait
          ? <LienCarte testId={`carte-commencer-${run.id}`} data-etat={bouton.etat} onClick={() => onCommencer(run)}>
              {bouton.libelle}
            </LienCarte>
          : pastilleConsignes}>
        {!fait && (<>
          {limite && <PhraseCarte testId={`carte-horaire-${run.id}`}>À finir avant <b>{limite}</b>.</PhraseCarte>}
          {/* À APPORTER : en lecture, tranché par la maquette (ligne 28091).
              L'agent n'a pas de stock : il lit, l'orchestrateur dépose. */}
          <BlocAApporter produits={info?.a_apporter} testId={`carte-a-apporter-${run.id}`} />
          {arrivants && (
            <div data-testid={`carte-arrivants-${run.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <Users size={17} strokeWidth={2} color={NEST.grisFonce} style={{ flexShrink: 0 }} />
              <span>{arrivants.principal}<span style={{ display: 'block', fontSize: 11, color: NEST.gris }}>{arrivants.secondaire}</span></span>
            </div>
          )}
          <LigneAcces adresse={info?.adresse_complete} cp={info?.code_postal} ville={info?.ville} code={code}
            testIdGps={`carte-gps-${run.id}`} testIdCode={`carte-code-${run.id}`} />
          {indisponible ? (
            <BoutonCarte testId={`carte-commencer-${run.id}`} grise disabled>Disponible le jour du ménage</BoutonCarte>
          ) : (<>
            <BoutonCarte testId={`carte-commencer-${run.id}`} data-etat={bouton.etat} onClick={() => onCommencer(run)}>
              {bouton.libelle}
            </BoutonCarte>
            <BoutonIncident testId={`carte-incident-${run.id}`} onClick={() => onIncident(run)} />
          </>)}
        </>)}
      </CartePlanning>
      {consignesOuvertes && (
        <FeuilleAgent titre={`Consigne · ${nom}`} testId={`feuille-consignes-${run.id}`} onFermer={() => setConsignesOuvertes(false)}>
          {consignes.map((c) => (
            <div key={c.id} style={{ background: NEST.fondCarte, borderRadius: 12, padding: 12, fontSize: 13, marginBottom: 8 }}>{c.title ? <b style={{ display: 'block' }}>{c.title}</b> : null}{c.body}</div>
          ))}
        </FeuilleAgent>
      )}
    </div>
  )
}
